import React, { FC, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { Row, Col, Card } from 'antd';
import styled from 'styled-components';
import useStore from '../hooks/useStore';
import ContentStore from '../stores/ContentStore';
import ProductStore from '../stores/ProductStore';
import TransactionsAndUsageTableDesktop from '../features/transaction-features/transactions-and-usage/TransactionsAndUsageTableDesktop';
import useIsMobileView from '../hooks/useIsMobileView';
import TransactionsAndUsageTableMobile from '../features/transaction-features/transactions-and-usage/TransactionsAndUsageTableMobile';
import { Transaction } from '../swagger/api';
import useFetchListDataWithPagination from '../hooks/fetchDataHooks/useFetchListDataWithPagination';
import AgreementsTable from '../features/transaction-features/transactions-and-usage/components/AgreementsTable';
import PageHeader from '../layout/components/PageHeader';
import PageHeaderContent from '../layout/components/PageHeaderContent';
import { setPageColor } from '../utils/wrapperAppFunctions';
import BalanceSettlementBanner from '../features/payment-features/BalanceSettlementBanner';
import useSettlementFlow from '../hooks/useSettlementFlow';

const StyledContent = styled.div`
  padding: 0 3% 0 3%;
  background-color: #fff;
  width: 100%;
  border: none;
`;

const SectionCard = styled(Card)<{ $isMobileView: boolean; $fullBleedOnMobile?: boolean }>`
  margin-bottom: 24px;
  border: none;

  ${(p) =>
    p.$isMobileView && p.$fullBleedOnMobile
      ? `
    .ant-card-body {
      margin: 0 -3%;
    }
  `
      : ''}

  .ant-card-head {
    border-bottom: ${(p) => (p.$isMobileView ? '1px' : '2px')} solid #efefef;
    padding-left: 0;
  }

  .ant-card-body {
    ${(p) =>
      p.$isMobileView
        ? 'padding: 0;'
        : `
      padding-top: 0;
      padding-inline: 0 !important;
    `}
  }

  .ant-card-head-title {
    font-family: Campton Semi Bold;
    font-weight: 600;
    font-size: 18px;
    color: #2d2d2d;
    padding-bottom: 25px;
  }
`;

const TransactionAndUsage: FC = () => {
  const contentStore = useStore(ContentStore) as ContentStore;
  const { transactionsStore, balanceStore } = contentStore.rootStore;
  const { handleSettlementClick } = useSettlementFlow(contentStore);
  const productStore = useStore(ProductStore) as ProductStore;
  const isMobileView = useIsMobileView();

  setPageColor();

  const { currentAccount } = contentStore.rootStore.userStore;

  const isTransactionsLoading = useFetchListDataWithPagination(
    transactionsStore.getAccountTransactions,
    transactionsStore.transactionsPageNumber
  );

  const transactions: Transaction[] = transactionsStore.accountTransactions;

  const showOutstandingOnly = false;
  const isLoading = isTransactionsLoading;

  const table = isMobileView ? (
    <TransactionsAndUsageTableMobile
      contentStore={contentStore}
      transactions={transactions}
      showOutstandingOnly={showOutstandingOnly}
      isTableOnProductCard={false}
      transactionsStore={transactionsStore}
      isLoading={isLoading}
    />
  ) : (
    <TransactionsAndUsageTableDesktop
      contentStore={contentStore}
      transactionsStore={transactionsStore}
      showOutstandingOnly={showOutstandingOnly}
      isTableOnProductCard={false}
      transactions={transactions}
      isLoading={isLoading}
      isPrivateCustomer
    />
  );

  const { productItemsStore } = productStore;

  useEffect(() => {
    if (productItemsStore.productItems.length === 0) {
      productItemsStore.getProducts();
    }
  }, [productItemsStore]);

  useEffect(() => {
    if (currentAccount?.id) {
      balanceStore.getBalance(currentAccount.id);
    }
  }, [currentAccount?.id, balanceStore]);

  const hasActiveAgreements = useMemo(
    () =>
      productItemsStore.productItems
        .flatMap((product) => product.servicesOnIdentifier || [])
        .filter((service) => service.name !== 'Storebælt Privat')
        .some((service) => service.isActive),
    [productItemsStore.productItems]
  );

  const showBalanceBanner = balanceStore.shouldShowBalanceBanner;

  return (
    <StyledContent>
      {showBalanceBanner && balanceStore.balance && (
        <BalanceSettlementBanner
          balance={balanceStore.balance}
          variant="private"
          cmsStore={contentStore.cmsStore}
          onSettlementClick={handleSettlementClick}
        />
      )}
      <PageHeader pageName={contentStore.cmsStore.getContent('TransactionAndUsage')?.content} />
      <PageHeaderContent cmsKey="PreviousTransactionAndUsage" />

      <Row gutter={[0, 24]} style={{ marginTop: '25px' }}>
        {hasActiveAgreements && (
          <Col span={24}>
            <SectionCard
              $isMobileView={isMobileView}
              $fullBleedOnMobile
              title={contentStore.cmsStore.getContent('DiscountAgreementsPrivateTable')?.content}
            >
              <AgreementsTable currentAccount={currentAccount} contentStore={contentStore} />
            </SectionCard>
          </Col>
        )}
        <Col span={24}>
          <SectionCard
            $isMobileView={isMobileView}
            title={contentStore.cmsStore.getContent('TransactionAndUsagePrivateTable')?.content}
          >
            {table}
          </SectionCard>
        </Col>
      </Row>
    </StyledContent>
  );
};

export default observer(TransactionAndUsage);
